import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Note, NoteFilter, NoteOrder, NoteOrderBy} from '../types/note';
import NoteComponent from './Note';
import {Link} from 'react-router-dom';
import SearchBar from './SearchBar';
import {useNotes} from '../hooks/useNotes';
import {format} from 'date-fns';

import {ALL_NOTES, ROOT_LEVEL} from "../utils/constants";

const ITEMS_PER_PAGE = 12;

interface NoteListProps {
  selectedCategoryId: number;
  categoryBreadcrumb: string;
}

export default function NoteList({ selectedCategoryId, categoryBreadcrumb }: NoteListProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const defaultFilter = useMemo(() => ({
    order_by: NoteOrderBy.created_at,
    order: NoteOrder.desc,
    skip: 0,
    limit: ITEMS_PER_PAGE,
    search: '',
    category_id: selectedCategoryId,
    start_date: undefined,
    end_date: undefined
  }), [selectedCategoryId]);

  const [activeFilter, setActiveFilter] = useState<NoteFilter>(defaultFilter);

  // Update filter when category changes
  useEffect(() => {
    setActiveFilter(prev => ({
      ...defaultFilter,
      category_id: selectedCategoryId
    }));
    setCurrentPage(1); // Reset to first page when category changes
  }, [selectedCategoryId]);

  // Use React Query hook
  const { notes, totalPages, isLoading, error, refetch } = useNotes({
    filter: activeFilter,
    page: currentPage
  });

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const handleFilterChange = useCallback((newFilter: NoteFilter) => {
    setActiveFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  }, []);

  const handleNoteUpdate = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleNoteDelete = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <SearchBar
        onFilterChange={handleFilterChange}
        selectedCategoryId={selectedCategoryId}
        categoryBreadcrumb={categoryBreadcrumb}
        initialFilter={activeFilter}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      )}

      {/* Error State */}
      {error instanceof Error && (
        <div className="text-red-500 text-center py-4">
          {error.message || 'An error occurred while loading notes. Please try again.'}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && notes.length === 0 && (
        <div className="text-center py-8">
          {(activeFilter.search || activeFilter.start_date || activeFilter.end_date) ? (
            // Filtered with no results
            <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No matching notes found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-2">
                  {activeFilter.search && (
                    <span>Search term: "{activeFilter.search}"<br /></span>
                  )}
                  {(activeFilter.start_date || activeFilter.end_date) && (
                    <span>
                      Date range: {activeFilter.start_date ? format(new Date(activeFilter.start_date), 'MMM d, yyyy') : 'Any'}
                      {' '} to {' '}{activeFilter.end_date ? format(new Date(activeFilter.end_date), 'MMM d, yyyy') : 'Any'}
                    </span>
                  )}
                  {categoryBreadcrumb && (
                    <span><br />Category: {categoryBreadcrumb}</span>
                  )}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  Try adjusting your filters to see more notes
                </p>
              </div>
            </div>
          ) : (
            // Empty category
            <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No notes yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {selectedCategoryId === ALL_NOTES.id && 'You have not created any notes yet.'}
                  {selectedCategoryId === ROOT_LEVEL.id && 'You have not created any notes in this category yet.'}
                  {selectedCategoryId !== ALL_NOTES.id && selectedCategoryId !== ROOT_LEVEL.id && `You have not created any notes in ${categoryBreadcrumb} yet.`}
                </p>
                <Link
                  to="/notes/new"
                  state={{ selectedCategoryId: selectedCategoryId, categoryBreadcrumb: categoryBreadcrumb }}
                  className="inline-block px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors"
                >
                  Create your first note
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notes Grid with Pagination */}
      {!isLoading && !error && notes.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map((note: Note) => (
              <NoteComponent
                key={note.id}
                note={note}
                onUpdate={handleNoteUpdate}
                onDelete={handleNoteDelete}
                onViewNote={() => window.location.href = `/notes/${note.id}/view`}
              />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-xl disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-xl disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}